// Что пользователь реально видит в навигации отдела.
//
// Дерево разделов живёт в lib/dept-tabs (buildDeptTabTree) — его же читает
// матрица прав. Здесь только отсечение по правам. Отдельный модуль, а не метод
// в dept-tabs: access.ts уже импортирует dept-tabs, и обратный импорт замкнул бы
// цикл.
//
// Один расчёт на всех: сайдбар считает список для ЛЮБОГО отдела (не только
// открытого), страница отдела по нему же решает, что рендерить. До 26.08.2026
// страница строила своё дерево параллельно ради иконок в баре разделов; бар
// уехал в сайдбар, иконки на этом уровне отпали, а вторая копия расчёта
// разошлась бы с первой при ближайшей правке прав.

import { buildDeptTabTree, type TabNode } from "@/lib/dept-tabs";
import { canViewTab, deptUsesTabPerms } from "@/lib/access";
import type { User } from "@/lib/types";

export function visibleDeptTabs(
  deptKey: string,
  deptModules: { key: string; title: string }[],
  user: User | null,
): TabNode[] {
  // tab-режим включается ТОЛЬКО если у пользователя есть tab-права на этот отдел;
  // иначе всё видно как раньше (deptModules уже отфильтрованы по module-view).
  if (!deptUsesTabPerms(user, deptKey)) return buildDeptTabTree(deptKey, deptModules);
  const visible: TabNode[] = [];
  for (const t of buildDeptTabTree(deptKey, deptModules)) {
    const children = t.children?.filter((c) => canViewTab(user, deptKey, t.key, c.key));
    // Вкладка остаётся, если доступна сама ИЛИ доступен хоть один её подраздел.
    if (!canViewTab(user, deptKey, t.key) && !(children ?? []).length) continue;
    visible.push(children ? { ...t, children } : t);
  }
  return visible;
}

/** Какой подраздел реально откроется в разделе.
 *
 *  ⚠️ ЕДИНСТВЕННЫЙ ОТВЕТ НА ВОПРОС «ЧТО ОТКРЫТО». Им же подсвечивается полоса
 *  подразделов, им же модуль получает `initialTab` — иначе подсветка и
 *  содержимое расходятся. Сохранённый ключ принимаем, только если он есть среди
 *  ВИДИМЫХ подразделов этого раздела (список уже отфильтрован по правам), иначе
 *  берём первый — тот, что подсвечен. Экрана, которого нет в полосе, у человека
 *  не появится: у него и права такого нет.
 */
export function resolveSubKey(
  subs: readonly { key: string }[] | undefined,
  saved: string | undefined,
): string | undefined {
  if (!subs?.length) return undefined;           // раздел без подразделов — модуль решает сам
  if (saved && subs.some((s) => s.key === saved)) return saved;
  return subs[0].key;
}
